import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, Button, Divider, Stepper, StepLabel, Step, stepConnectorClasses, StepConnector, Typography,
  Alert, useTheme, useMediaQuery,
} from '@mui/material';
import CustomStepIcon from '../form-stepper/CustomStepIcon';
import authService from '../Auth/AuthService';

const info = JSON.parse(localStorage.getItem("device")) || null;

const initialEmpData = {
  aadhaarNumber: "",
  teacherCode: "",
  employeeCode: "",
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  gender: "",
  dateOfBirth: "",
  addressDTO: {
    addressLine1: "",
    addressLine2: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    latitude: "",
    longitude: "",
  },
  accountDto: {
    type: '',
    acctnumber: '',
    branch: '',
    ifsccode: '',
    pafnumber: '',
    bankname: '',
    pannumber: '',
  },
  qualification: "",
  specialization: "",
  joiningDate: "",
  salary: "",
  maritalStatus: "",
  experience: "",
  photo: "",
  company: "",
  degree: "",
  tongue: "",
};

const steps = ["Personal Info", "Account Info", "Address Info"];

const form1 = [
  { label: 'Aadhaar Number', name: 'aadhaarNumber', placeholder: 'Enter 12-digit Aadhaar number', req: true },
  { label: 'First Name', name: 'firstName', placeholder: 'Enter first name', req: true },
  { label: 'Father/husband Name', name: 'middleName', placeholder: 'Enter father/husband name', req: true },
  { label: 'Last Name', name: 'lastName', placeholder: 'Enter last name', req: true },
  { label: 'Email', name: 'email', placeholder: 'Enter email address', req: true },
  { label: 'Phone', name: 'phone', placeholder: 'Enter 10-digit mobile number', req: true },
  { label: 'Gender', name: 'gender', placeholder: 'Enter MALE/FEMALE/OTHER', req: true },
  { label: 'Date Of Birth', name: 'dateOfBirth', type: 'date', placeholder: 'Select date of birth', req: true },
  { label: 'Marital Status', name: 'maritalStatus', placeholder: 'Enter married/single', req: true },
  { label: 'Qualification', name: 'qualification', placeholder: 'Enter highest qualification', req: true },
  { label: 'Specialization', name: 'specialization', placeholder: 'Enter special subject', req: true },
  { label: 'Salary', name: 'salary', type: 'number', placeholder: 'Enter monthly salary amount', req: true },
  { label: 'Experience', name: 'experience', placeholder: 'Enter years of experience', req: true },
  { label: 'Previous School Name', name: 'company', placeholder: 'Enter previous school name', req: false },
  { label: 'Degree', name: 'degree', placeholder: 'Enter degree/b.ed./d.ed', req: false },
  { label: 'Mother Tongue', name: 'tongue', placeholder: 'Enter your mother tongue', req: true },
];

const form2 = [
  { label: 'Address Line 1', name: 'addressLine1', placeholder: 'House no, street, area', req: true },
  { label: 'Address Line 2', name: 'addressLine2', placeholder: 'Landmark (optional)', req: false },
  { label: 'Pincode', name: 'pincode', placeholder: 'Enter pincode', req: true },
  { label: 'City', name: 'city', placeholder: 'Enter city', req: true },
  { label: 'State', name: 'state', placeholder: 'Enter state', req: true },
  { label: 'Country', name: 'country', placeholder: 'Enter country', req: false },
  { label: 'Latitude', name: 'latitude', placeholder: 'Auto or manual latitude', req: false },
  { label: 'Longitude', name: 'longitude', placeholder: 'Auto or manual longitude', req: false },
  { label: 'Joining Date', name: 'joiningDate', type: 'date', placeholder: 'Select joining date', req: true },
];

const form3 = [
  { label: 'Account Type', name: 'type', placeholder: 'Savings / Current', req: true },
  { label: 'Account Number', name: 'acctnumber', placeholder: 'Enter account number', req: true },
  { label: 'Branch Name', name: 'branch', placeholder: 'Enter branch name', req: true },
  { label: 'IFSC Code', name: 'ifsccode', placeholder: 'Enter IFSC code', req: true },
  { label: 'Bank Name', name: 'bankname', placeholder: 'Enter bank name', req: true },
  { label: 'PAN Number', name: 'pannumber', placeholder: 'Enter PAN number', req: false },
  { label: 'PF Number', name: 'pafnumber', placeholder: 'Enter PF number', req: false },
];

const ACCOUNT_TYPES = ["Savings", "Current"];

export default function TeacherRegister({ onSuccess, editData = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isEditMode = Boolean(editData && editData.id);

  const buildInitialState = () => {
    if (!editData) return initialEmpData;
    return {
      ...initialEmpData,
      ...editData,
      addressDTO: {
        ...initialEmpData.addressDTO,
        ...(editData.addressDTO || {}),
      },
      accountDto: {
        ...initialEmpData.accountDto,
        ...(editData.accountDto || {}),
      },
    };
  };

  const [employee, setEmployee] = useState(buildInitialState);
  const [pages, setPages] = useState(0);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setEmployee(buildInitialState());
    setPages(0);
    setError(null);
    setMessage(null);
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeAddress = (e) => {
    const { name, value } = e.target;
    setEmployee((prev) => ({
      ...prev,
      addressDTO: {
        ...prev.addressDTO,
        [name]: value
      }
    }));
  };

  const handleChangeAccount = (e) => {
    const { name, value } = e.target;

    if (name === 'acctnumber' && value !== '' && !/^\d*$/.test(value)) {
      return; // ignore the keystroke
    }
    setEmployee((prev) => ({
      ...prev,
      accountDto: {
        ...prev.accountDto,
        [name]: value
      }
    }));
  };

  const handleSetAddress = () => {
    const hasLat = employee.addressDTO.latitude;
    const hasLng = employee.addressDTO.longitude;

    if (hasLat && hasLng) return true;

    if (!info) {
      setMessage({
        type: 'error',
        text: "This app requires access to your location. Please enable location permission to proceed."
      });
      return false;
    }

    setEmployee((prev) => ({
      ...prev,
      addressDTO: {
        ...prev.addressDTO,
        latitude: info.latitude,
        longitude: info.longitude,
      }
    }));
    return true;
  };

  const nextPage = () => setPages(pages + 1);
  const prevPage = () => setPages(pages - 1);

  const getFieldValue = (field, source) => (source ? employee[source][field.name] : employee[field.name]);

  const isSectionValid = (formFields, source = null) => {
    for (let field of formFields) {
      if (field.req) {
        const value = getFieldValue(field, source);
        if (!value || value.toString().trim() === '') return false;
      }
    }
    return true;
  };

  const isValidAccountType = (value) => ACCOUNT_TYPES.includes(value);
  const isValidAccountNumber = (value) => /^\d{9,18}$/.test(value.toString().trim());

  const validateFormSection = (formFields, source = null) => {
    for (let field of formFields) {
      if (field.req) {
        const value = getFieldValue(field, source);
        if (!value || value.toString().trim() === '') {
          const msg = `Please fill out the "${field.label}" field.`;
          setError(msg);
          setMessage({ type: 'error', text: msg });
          return false;
        }
        if (field.name === 'type' && source === 'accountDto' && !isValidAccountType(value)) {
          const msg = `Account Type must be "Savings" or "Current".`;
          setError(msg);
          setMessage({ type: 'error', text: msg });
          return false;
        }
        if (field.name === 'acctnumber' && source === 'accountDto' && !isValidAccountNumber(value)) {
          const msg = `Account Number must be 9 to 18 digits.`;
          setError(msg);
          setMessage({ type: 'error', text: msg });
          return false;
        }
      }
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFormSection(form2, 'addressDTO')) return;
    if (!handleSetAddress()) return;

    console.log(`employee ${isEditMode ? 'Update' : 'Registration'} Data:`, employee);

    try {
      let url, response;

      if (isEditMode) {
        url = authService.pathUrl("PUT", `/employees/${employee.id}`);
        response = await authService.putAuth(url, employee);
      } else {
        url = authService.pathUrl("POST", "/employees");
        response = await authService.postAuth(url, employee);
      }

      console.log('Success:', response);
      setMessage({
        type: 'success',
        text: isEditMode ? 'Teacher updated successfully.' : 'Teacher registered successfully.'
      });

      if (onSuccess) {
        onSuccess(response);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Error:', err);
      setMessage({
        type: 'error',
        text: err.message
      });
    }
  };

  const CustomConnector = (props) => (
    <StepConnector
      {...props}
      sx={{
        [`& .${stepConnectorClasses.line}`]: {
          borderColor: '#ccc',
          borderTopWidth: 3,
        },
        [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
          borderColor: 'green',
        },
        [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
          borderColor: 'green',
        },
      }}
    />
  );

  const buttonStyle = {
    minWidth: 160,
    py: 1,
    borderRadius: 6,
    backgroundColor: "#1b1914",
    "&:hover": {
      backgroundColor: "#332a14ff",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cfc9bd",
    },
  };

  const handleNextFromStep0 = () => {
    if (!validateFormSection(form1)) return;
    nextPage();
  };
  const handleNextFromStep1 = () => {
    if (!validateFormSection(form3, 'accountDto')) return;
    nextPage();
  };

  const renderStep = () => {
    switch (pages) {
      case 0:
        return (
          <>
            <Grid container spacing={2} justifyContent={'space-around'}>
              {form1.map(({ label, name, type = "text", req = true, placeholder }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    fullWidth
                    placeholder={placeholder}
                    sx={{ width: 230 }}
                    size={isMobile ? "small" : "medium"}
                    required={req}
                    label={label}
                    name={name}
                    value={employee[name] || ""}
                    onChange={handleChange}
                    type={type}
                    slotProps={
                      type === "date"
                        ? { inputLabel: { shrink: true } }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {error && (
              <Typography sx={{ fontSize: 12, color: "red", mt: 1 }}>
                {error}
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                onClick={handleNextFromStep0}
                disabled={!isSectionValid(form1)}
                sx={buttonStyle}
              >
                Next
              </Button>
            </Box>
          </>
        );
      case 1:
        return (
          <>
            <Grid container spacing={2} justifyContent={'space-around'}>
              {form3.map(({ label, name, type = "text", req = true, placeholder }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    fullWidth
                    placeholder={placeholder}
                    sx={{ width: 230 }}
                    size={isMobile ? "small" : "medium"}
                    required={req}
                    label={label}
                    name={name}
                    value={employee.accountDto[name] || ""}
                    onChange={handleChangeAccount}
                    type={type}
                    slotProps={
                      type === "date"
                        ? { inputLabel: { shrink: true } }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {error && (
              <Typography sx={{ fontSize: 12, color: "red", mt: 1 }}>
                {error}
              </Typography>
            )}

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                variant="contained"
                onClick={prevPage}
                sx={buttonStyle}
              >
                Prev
              </Button>
              <Button
                variant="contained"
                onClick={handleNextFromStep1}
                disabled={!isSectionValid(form3, 'accountDto')}
                sx={buttonStyle}
              >
                Next
              </Button>
            </Box>
          </>
        );

      case 2:
        return (
          <>
            <Grid container spacing={2} justifyContent={'space-around'}>
              {form2.map(({ label, name, type = "text", req = true, placeholder }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    fullWidth
                    placeholder={placeholder}
                    sx={{ width: 230 }}
                    size={isMobile ? "small" : "medium"}
                    required={req}
                    label={label}
                    name={name}
                    value={employee.addressDTO[name] || ""}
                    onChange={handleChangeAddress}
                    type={type}
                    slotProps={
                      type === "date"
                        ? { inputLabel: { shrink: true } }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>

            {error && (
              <Typography sx={{ fontSize: 12, color: "red", mt: 1 }}>
                {error}
              </Typography>
            )}

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                variant="contained"
                onClick={prevPage}
                sx={buttonStyle}
              >
                Prev
              </Button>

              <Button
                variant="contained"
                type="submit"
                disabled={!isSectionValid(form2, 'addressDTO')}
                sx={buttonStyle}
              >
                {isEditMode ? "Update" : "Submit"}
              </Button>
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ background: "#FFF", p: 1 }}>
      <Box sx={{ width: "100%", px: 1 }}>

        <Typography variant="h6" sx={{ mb: 1 }}>
          {isEditMode ? "Update Teacher" : "Register Teacher"}
        </Typography>

        <Stepper
          activeStep={pages}
          alternativeLabel
          connector={<CustomConnector />}
          sx={{ mb: 2 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          {renderStep()}

          {message && (
            <Alert
              severity={message.type === "error" ? "error" : "success"}
              sx={{ mt: 3, borderRadius: 2 }}
            >
              {message.text}
            </Alert>
          )}
        </form>
      </Box>
    </Box>
  );
}